import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { List, Map } from 'immutable'
import _isFunction from 'lodash/isFunction'
import IconV2, { ICON_TYPES } from '../Icon.v2/index'

/**
 * SelectOption
 * @param {Object} props - The props
 * @param {Function} props.onClick - Callback function from parent
 * @param {Map} props.value - Value, Map consists of name, value fields
 * @param {Boolean} props.active - Is current item active, to mark it
 * @returns {JSX.Element} - Option for SelectField
 */
const SelectOption = ({ value, onClick, active }) => (
  <div
    className={`select-field__options-item ${active ? 'select-field__options-item--active' : ''}`}
    key={value.get('name')}
    onClick={() => onClick(value)}
  >
    {value.get('name', '')}
  </div>
)

/**
 * SelectField
 * @param {Object} props - The props
 * @param {Function} props.onClick - Callback function from parent
 * @param {List} props.options - List of option items, with name, value
 * @param {String || Number} props.defaultValue - Default/initial value for field
 * @returns {JSX.Element} - Select field
 */
const SelectField = ({ onClick, options = List(), defaultValue }) => {
  const selectedDefaultOption = useMemo(() => {
    const item = options.find((option) => option.get('value') === defaultValue)

    return item || options.first() || Map()
  }, [defaultValue])

  const [selectedOption, setSelectedOption] = useState(selectedDefaultOption)
  const [opened, setOpened] = useState(false)

  const fieldRef = useRef(null)

  // close on click outside
  useEffect(() => {
    const handleOutsideClick = ({ target }) => {
      if (!fieldRef.current || fieldRef.current.contains(target)) {
        return
      }

      setOpened(false)
    }

    global.addEventListener('click', handleOutsideClick)

    return () => { global.removeEventListener('click', handleOutsideClick) }
  }, [])

  if (!options.size) {
    return null
  }

  const handleChange = (option) => {
    setSelectedOption(option)
    setOpened(false)

    if (_isFunction(onClick)) {
      onClick(option)
    }
  }

  const handleOpen = () => {
    setOpened((currentValue) => !currentValue)
  }

  return (
    <div className="select-field" ref={fieldRef}>
      <div
        className={`select-field__field ${opened ? 'select-field__field--opened' : ''}`}
        onClick={handleOpen}
      >
        <div>
          <span>{selectedOption.get('name', '')}</span>
        </div>
        <div>
          <IconV2 type={opened ? ICON_TYPES.ARROW_UP : ICON_TYPES.ARROW_DOWN} />
        </div>
      </div>
      {opened && (
        <div className="select-field__options">
          {options.map((option, index) => (
            <SelectOption
              key={option.get('name', index)}
              value={option}
              active={option.get('value') === selectedOption.get('value')}
              onClick={handleChange}
            />
          ))}
        </div>
      )}
    </div>
  )
}

SelectField.propTypes = {
  onClick: PropTypes.func,
  options: ImmutablePropTypes.list.isRequired,
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
}

export default SelectField
